import type { CellData, ColorCode } from '@flipboard/shared';
import {
  getCellChar,
  getCellColor,
  getCellBg,
  createEmptyCells,
  parseColorMarkup,
  COLOR_CODES,
  COLOR_MAP,
  CHARSET,
} from '@flipboard/shared';

export type ColorChangeCallback = (color: ColorCode) => void;
export type BgChangeCallback = (bg: ColorCode | null) => void;
export type ModeChangeCallback = (mode: EditorMode) => void;
export type ClearCallback = () => void;
export type CenterAlignCallback = () => void;
export type EditorMode = 'wysiwyg' | 'raw';

/**
 * WYSIWYG Board Editor
 * A visual grid editor for split-flap board content
 */
export class BoardEditor {
  private container: HTMLElement;
  private gridElement: HTMLElement;
  private rawTextarea: HTMLTextAreaElement;
  private focusInput: HTMLInputElement;
  private cells: HTMLElement[][] = [];

  // State
  private cellData: CellData[][] = [];
  private rows: number;
  private cols: number;
  private cursorRow = 0;
  private cursorCol = 0;
  private selectedColor: ColorCode = 'W';
  private selectedBg: ColorCode | null = null;
  private mode: EditorMode = 'wysiwyg';

  // Selection state
  private selectionStart: { row: number; col: number } | null = null;
  private selectionEnd: { row: number; col: number } | null = null;
  private isDragging = false;

  // Internal clipboard
  private clipboard: CellData[][] | null = null;

  // Callbacks
  private onColorChange?: ColorChangeCallback;
  private onBgChange?: BgChangeCallback;

  constructor(container: HTMLElement, rows: number, cols: number) {
    this.container = container;
    this.rows = rows;
    this.cols = cols;

    // Initialize empty cell data
    this.cellData = createEmptyCells(rows, cols);

    // Build DOM structure
    this.gridElement = document.createElement('div');
    this.gridElement.className = 'board-editor-grid';
    this.container.appendChild(this.gridElement);

    // Raw text textarea (hidden by default)
    this.rawTextarea = document.createElement('textarea');
    this.rawTextarea.className = 'board-editor-raw';
    this.rawTextarea.placeholder = 'Type message with color codes: {R}red {G}green {W}white';
    this.rawTextarea.rows = rows;
    this.rawTextarea.style.display = 'none';
    this.container.appendChild(this.rawTextarea);

    // Hidden input for keyboard capture
    this.focusInput = document.createElement('input');
    this.focusInput.className = 'editor-focus-trap';
    this.focusInput.setAttribute('aria-label', 'Board editor input');
    this.focusInput.style.cssText = `
      position: absolute;
      left: -9999px;
      opacity: 0;
      width: 1px;
      height: 1px;
    `;
    this.container.appendChild(this.focusInput);

    // Event listeners
    this.focusInput.addEventListener('keydown', this.handleKeyDown.bind(this));
    this.focusInput.addEventListener('blur', () => {
      // Re-focus after a short delay to keep cursor visible
      // Only if we're still in the board view and in WYSIWYG mode
      // BUT don't steal focus from other interactive elements
      setTimeout(() => {
        const activeEl = document.activeElement;
        const isOtherInput =
          activeEl instanceof HTMLInputElement ||
          activeEl instanceof HTMLTextAreaElement ||
          activeEl instanceof HTMLSelectElement ||
          activeEl instanceof HTMLButtonElement;

        if (
          this.container.offsetParent !== null &&
          this.mode === 'wysiwyg' &&
          !isOtherInput
        ) {
          this.focusInput.focus();
        }
      }, 10);
    });

    // Click on container focuses the input
    this.container.addEventListener('click', (e) => {
      if (e.target === this.container || e.target === this.gridElement) {
        this.focus();
      }
    });

    // Build grid
    this.buildGrid();

    // Grid-level mouse event listeners for drag selection
    this.gridElement.addEventListener('mousemove', this.handleMouseMove.bind(this));
    this.gridElement.addEventListener('mouseup', this.handleMouseUp.bind(this));
    this.gridElement.addEventListener('mouseleave', this.handleMouseUp.bind(this));

    this.render();
  }

  /**
   * Get normalized selection bounds (top-left to bottom-right)
   */
  private getSelectionBounds(): { startRow: number; startCol: number; endRow: number; endCol: number } | null {
    if (!this.selectionStart || !this.selectionEnd) return null;
    return {
      startRow: Math.min(this.selectionStart.row, this.selectionEnd.row),
      startCol: Math.min(this.selectionStart.col, this.selectionEnd.col),
      endRow: Math.max(this.selectionStart.row, this.selectionEnd.row),
      endCol: Math.max(this.selectionStart.col, this.selectionEnd.col),
    };
  }

  /**
   * Check if a cell is within selection
   */
  private isCellSelected(row: number, col: number): boolean {
    const bounds = this.getSelectionBounds();
    if (!bounds) return false;
    return row >= bounds.startRow && row <= bounds.endRow && col >= bounds.startCol && col <= bounds.endCol;
  }

  /**
   * Check if we have an active multi-cell selection
   */
  private hasMultiCellSelection(): boolean {
    const bounds = this.getSelectionBounds();
    if (!bounds) return false;
    return bounds.startRow !== bounds.endRow || bounds.startCol !== bounds.endCol;
  }

  /**
   * Clear the selection
   */
  private clearSelection(): void {
    this.selectionStart = null;
    this.selectionEnd = null;
  }

  /**
   * Get all cells within given bounds
   */
  private getCellsInBounds(bounds: { startRow: number; startCol: number; endRow: number; endCol: number }): Array<{ row: number; col: number }> {
    const cells: Array<{ row: number; col: number }> = [];
    for (let row = bounds.startRow; row <= bounds.endRow; row++) {
      for (let col = bounds.startCol; col <= bounds.endCol; col++) {
        cells.push({ row, col });
      }
    }
    return cells;
  }

  /**
   * Build the grid DOM structure
   */
  private buildGrid(): void {
    this.gridElement.innerHTML = '';
    this.gridElement.style.gridTemplateColumns = `repeat(${this.cols}, 1fr)`;
    this.cells = [];

    for (let row = 0; row < this.rows; row++) {
      const rowCells: HTMLElement[] = [];
      for (let col = 0; col < this.cols; col++) {
        const cell = document.createElement('div');
        cell.className = 'editor-cell';
        cell.dataset.row = String(row);
        cell.dataset.col = String(col);

        // Cell inner structure
        cell.innerHTML = `
          <div class="cell-flap">
            <span class="cell-char"></span>
            <div class="cell-divider"></div>
          </div>
        `;

        // Mousedown handler for drag selection
        cell.addEventListener('mousedown', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.handleCellMouseDown(row, col, e);
        });

        this.gridElement.appendChild(cell);
        rowCells.push(cell);
      }
      this.cells.push(rowCells);
    }
  }

  /**
   * Handle mouse down on cell (start drag selection)
   */
  private handleCellMouseDown(row: number, col: number, _e: MouseEvent): void {
    this.isDragging = true;
    this.selectionStart = { row, col };
    this.selectionEnd = { row, col };

    // Move cursor to click position
    this.cursorRow = row;
    this.cursorCol = col;

    // Add selecting class for visual feedback
    this.gridElement.classList.add('selecting');

    this.render();
    this.focus();
  }

  /**
   * Handle mouse move (update drag selection)
   */
  private handleMouseMove(e: MouseEvent): void {
    if (!this.isDragging) return;

    // Find which cell the mouse is over
    const target = e.target as HTMLElement;
    const cell = target.closest('.editor-cell') as HTMLElement;
    if (!cell) return;

    const row = parseInt(cell.dataset.row || '0', 10);
    const col = parseInt(cell.dataset.col || '0', 10);

    // Update selection end
    if (this.selectionEnd?.row !== row || this.selectionEnd?.col !== col) {
      this.selectionEnd = { row, col };
      this.render();
    }
  }

  /**
   * Handle mouse up (end drag selection)
   */
  private handleMouseUp(_e: MouseEvent): void {
    if (!this.isDragging) return;
    this.isDragging = false;

    // Remove selecting class
    this.gridElement.classList.remove('selecting');

    // If it was just a click (no drag), clear multi-selection
    if (
      this.selectionStart &&
      this.selectionEnd &&
      this.selectionStart.row === this.selectionEnd.row &&
      this.selectionStart.col === this.selectionEnd.col
    ) {
      this.clearSelection();
    }

    this.render();
  }

  /**
   * Update toolbar colors based on current cursor cell
   */
  private updateToolbarFromCursor(): void {
    const cellData = this.cellData[this.cursorRow][this.cursorCol];
    const color = (getCellColor(cellData) || 'W') as ColorCode;
    const bg = (getCellBg(cellData) || null) as ColorCode | null;

    this.selectedColor = color;
    this.selectedBg = bg;

    if (this.onColorChange) {
      this.onColorChange(color);
    }
    if (this.onBgChange) {
      this.onBgChange(bg);
    }
  }

  /**
   * Render current state to DOM
   */
  private render(): void {
    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const cellElement = this.cells[row][col];
        const cellData = this.cellData[row][col];
        const char = getCellChar(cellData);
        const color = (getCellColor(cellData) || 'W') as ColorCode;
        const bg = getCellBg(cellData) as ColorCode | undefined;

        // Update character
        const charSpan = cellElement.querySelector('.cell-char') as HTMLElement;
        charSpan.textContent = char === ' ' ? '' : char;

        // Update text color
        cellElement.dataset.color = color;

        // Update background color
        const flapElement = cellElement.querySelector('.cell-flap') as HTMLElement;
        const bgColors: Record<string, string> = {
          W: '#f5f5f0',
          R: '#ef4444',
          G: '#22c55e',
          B: '#3b82f6',
          Y: '#eab308',
          O: '#f97316',
          P: '#a855f7',
          C: '#06b6d4',
          K: '#ec4899',
        };
        if (bg && bgColors[bg]) {
          cellElement.dataset.bg = bg;
          cellElement.classList.add('has-bg');
          // Apply to both elements and use background-color specifically
          if (flapElement) {
            flapElement.style.setProperty('background-color', bgColors[bg], 'important');
            flapElement.style.setProperty('background-image', 'none', 'important');
          }
        } else {
          delete cellElement.dataset.bg;
          cellElement.classList.remove('has-bg');
          if (flapElement) {
            flapElement.style.removeProperty('background-color');
            flapElement.style.removeProperty('background-image');
          }
        }

        // Update cursor
        const isCursor = row === this.cursorRow && col === this.cursorCol;
        cellElement.classList.toggle('cursor', isCursor);

        // Update selection
        const isSelected = this.isCellSelected(row, col);
        cellElement.classList.toggle('selected', isSelected);
      }
    }

    // Update toolbar colors to match current cursor cell
    this.updateToolbarFromCursor();
  }

  /**
   * Handle keyboard input
   */
  private handleKeyDown(e: KeyboardEvent): void {
    // Copy: Ctrl/Cmd + C
    if ((e.metaKey || e.ctrlKey) && e.key === 'c') {
      e.preventDefault();
      this.copySelection();
      return;
    }

    // Paste: Ctrl/Cmd + V
    if ((e.metaKey || e.ctrlKey) && e.key === 'v') {
      e.preventDefault();
      this.pasteClipboard();
      return;
    }

    // Cut: Ctrl/Cmd + X
    if ((e.metaKey || e.ctrlKey) && e.key === 'x') {
      e.preventDefault();
      this.copySelection();
      this.deleteSelection();
      return;
    }

    // Select All: Ctrl/Cmd + A
    if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
      e.preventDefault();
      this.selectAll();
      return;
    }

    // Color shortcuts: Cmd/Ctrl + 1-9
    if ((e.metaKey || e.ctrlKey) && e.key >= '1' && e.key <= '9') {
      e.preventDefault();
      const colorIndex = parseInt(e.key) - 1;
      if (colorIndex < COLOR_CODES.length) {
        this.setSelectedColor(COLOR_CODES[colorIndex]);
      }
      return;
    }

    // Navigation and editing
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        this.moveCursor(0, -1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        this.moveCursor(0, 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        this.moveCursor(-1, 0);
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.moveCursor(1, 0);
        break;
      case 'Tab':
        e.preventDefault();
        if (e.shiftKey) {
          this.moveCursorBackward();
        } else {
          this.moveCursorForward();
        }
        break;
      case 'Enter':
        e.preventDefault();
        // Move to start of next row
        this.cursorCol = 0;
        this.moveCursor(0, 1);
        break;
      case 'Backspace':
        e.preventDefault();
        if (this.hasMultiCellSelection()) {
          this.deleteSelection();
        } else {
          this.handleBackspace();
        }
        break;
      case 'Delete':
        e.preventDefault();
        if (this.hasMultiCellSelection()) {
          this.deleteSelection();
        } else {
          this.clearCurrentCell();
        }
        break;
      case 'Home':
        e.preventDefault();
        this.cursorCol = 0;
        this.render();
        break;
      case 'End':
        e.preventDefault();
        this.cursorCol = this.cols - 1;
        this.render();
        break;
      default:
        // Character input - only single printable characters
        if (e.key.length === 1 && !e.metaKey && !e.ctrlKey) {
          e.preventDefault();
          // If multi-selection exists, collapse to single cursor at start
          if (this.hasMultiCellSelection()) {
            const bounds = this.getSelectionBounds()!;
            this.cursorRow = bounds.startRow;
            this.cursorCol = bounds.startCol;
            this.clearSelection();
          }
          this.typeCharacter(e.key);
        }
    }
  }

  /**
   * Copy selection to internal clipboard
   */
  private copySelection(): void {
    const bounds = this.getSelectionBounds();
    if (!bounds) {
      // Copy single cell at cursor
      this.clipboard = [[this.cellData[this.cursorRow][this.cursorCol]]];
      return;
    }

    // Copy rectangular region
    this.clipboard = [];
    for (let row = bounds.startRow; row <= bounds.endRow; row++) {
      const rowData: CellData[] = [];
      for (let col = bounds.startCol; col <= bounds.endCol; col++) {
        // Deep copy cell data
        const cell = this.cellData[row][col];
        rowData.push(typeof cell === 'string' ? cell : { ...cell });
      }
      this.clipboard.push(rowData);
    }
  }

  /**
   * Paste clipboard at cursor position
   */
  private pasteClipboard(): void {
    if (!this.clipboard || this.clipboard.length === 0) return;

    // Paste at cursor position, respecting grid bounds
    const startRow = this.cursorRow;
    const startCol = this.cursorCol;

    for (let r = 0; r < this.clipboard.length; r++) {
      const targetRow = startRow + r;
      if (targetRow >= this.rows) break;

      for (let c = 0; c < this.clipboard[r].length; c++) {
        const targetCol = startCol + c;
        if (targetCol >= this.cols) break;

        // Deep copy cell data
        const cell = this.clipboard[r][c];
        this.cellData[targetRow][targetCol] = typeof cell === 'string' ? cell : { ...cell };
      }
    }

    // Clear selection and re-render
    this.clearSelection();
    this.render();
  }

  /**
   * Delete all cells in selection
   */
  private deleteSelection(): void {
    const bounds = this.getSelectionBounds();
    if (!bounds) {
      this.clearCurrentCell();
      return;
    }

    // Clear all cells in selection
    for (let row = bounds.startRow; row <= bounds.endRow; row++) {
      for (let col = bounds.startCol; col <= bounds.endCol; col++) {
        this.cellData[row][col] = ' ';
      }
    }

    // Move cursor to selection start and clear selection
    this.cursorRow = bounds.startRow;
    this.cursorCol = bounds.startCol;
    this.clearSelection();
    this.render();
  }

  /**
   * Select all cells
   */
  private selectAll(): void {
    this.selectionStart = { row: 0, col: 0 };
    this.selectionEnd = { row: this.rows - 1, col: this.cols - 1 };
    this.render();
  }

  /**
   * Center-align content within selection
   */
  centerSelection(): void {
    const bounds = this.getSelectionBounds();
    if (!bounds) {
      // No selection - do nothing
      return;
    }

    const selectionWidth = bounds.endCol - bounds.startCol + 1;

    for (let row = bounds.startRow; row <= bounds.endRow; row++) {
      // Extract ALL content from this row within selection
      const allContent: CellData[] = [];
      for (let col = bounds.startCol; col <= bounds.endCol; col++) {
        allContent.push(this.cellData[row][col]);
      }

      // Find first and last non-space positions to trim
      let firstNonSpace = -1;
      let lastNonSpace = -1;
      for (let i = 0; i < allContent.length; i++) {
        const char = getCellChar(allContent[i]);
        if (char !== ' ') {
          if (firstNonSpace === -1) firstNonSpace = i;
          lastNonSpace = i;
        }
      }

      // If no content in this row, skip
      if (firstNonSpace === -1) continue;

      // Extract trimmed content (preserving internal spaces)
      const trimmedContent = allContent.slice(firstNonSpace, lastNonSpace + 1);
      const contentWidth = trimmedContent.length;
      const leftPadding = Math.floor((selectionWidth - contentWidth) / 2);

      // Clear the selection region for this row
      for (let col = bounds.startCol; col <= bounds.endCol; col++) {
        this.cellData[row][col] = ' ';
      }

      // Place centered content
      for (let i = 0; i < trimmedContent.length; i++) {
        const targetCol = bounds.startCol + leftPadding + i;
        if (targetCol <= bounds.endCol) {
          this.cellData[row][targetCol] = trimmedContent[i];
        }
      }
    }

    this.render();
  }

  /**
   * Move cursor by delta
   */
  private moveCursor(dx: number, dy: number): void {
    // Clear selection when navigating
    this.clearSelection();

    const newCol = this.cursorCol + dx;
    const newRow = this.cursorRow + dy;

    if (newCol >= 0 && newCol < this.cols) {
      this.cursorCol = newCol;
    }
    if (newRow >= 0 && newRow < this.rows) {
      this.cursorRow = newRow;
    }

    this.render();
  }

  /**
   * Move cursor forward with row wrapping
   */
  private moveCursorForward(): void {
    // Clear selection when navigating
    this.clearSelection();

    this.cursorCol++;
    if (this.cursorCol >= this.cols) {
      this.cursorCol = 0;
      this.cursorRow++;
      if (this.cursorRow >= this.rows) {
        this.cursorRow = this.rows - 1;
        this.cursorCol = this.cols - 1;
      }
    }
    this.render();
  }

  /**
   * Move cursor backward with row wrapping
   */
  private moveCursorBackward(): void {
    // Clear selection when navigating
    this.clearSelection();

    this.cursorCol--;
    if (this.cursorCol < 0) {
      this.cursorCol = this.cols - 1;
      this.cursorRow--;
      if (this.cursorRow < 0) {
        this.cursorRow = 0;
        this.cursorCol = 0;
      }
    }
    this.render();
  }

  /**
   * Type a character at cursor position
   */
  private typeCharacter(char: string): void {
    // Normalize to uppercase and check if valid
    const upperChar = char.toUpperCase();
    const normalizedChar = CHARSET.includes(upperChar) ? upperChar : ' ';

    // Set cell data with current color and background
    const hasColor = this.selectedColor !== 'W';
    const hasBg = this.selectedBg !== null;

    if (!hasColor && !hasBg) {
      this.cellData[this.cursorRow][this.cursorCol] = normalizedChar;
    } else if (hasColor && !hasBg) {
      this.cellData[this.cursorRow][this.cursorCol] = {
        char: normalizedChar,
        color: this.selectedColor,
      };
    } else if (!hasColor && hasBg) {
      this.cellData[this.cursorRow][this.cursorCol] = {
        char: normalizedChar,
        bg: this.selectedBg!,
      };
    } else {
      this.cellData[this.cursorRow][this.cursorCol] = {
        char: normalizedChar,
        color: this.selectedColor,
        bg: this.selectedBg!,
      };
    }

    // Move cursor forward
    this.moveCursorForward();
  }

  /**
   * Handle backspace - clear cell and move back
   */
  private handleBackspace(): void {
    // If at start, just clear current cell
    if (this.cursorRow === 0 && this.cursorCol === 0) {
      this.clearCurrentCell();
      return;
    }

    // Move back first
    this.moveCursorBackward();
    // Then clear
    this.clearCurrentCell();
  }

  /**
   * Clear current cell
   */
  private clearCurrentCell(): void {
    this.cellData[this.cursorRow][this.cursorCol] = ' ';
    this.render();
  }

  /**
   * Focus the editor
   */
  focus(): void {
    this.focusInput.focus();
  }

  /**
   * Set the selected text color
   * Also applies the color to selected cells (or current cursor cell if no selection)
   */
  setSelectedColor(color: ColorCode): void {
    if (this.selectedColor === color) return; // Prevent loops

    this.selectedColor = color;

    // Apply to all selected cells (or just cursor cell if no selection)
    const bounds = this.getSelectionBounds();
    const cellsToUpdate = bounds
      ? this.getCellsInBounds(bounds)
      : [{ row: this.cursorRow, col: this.cursorCol }];

    for (const { row, col } of cellsToUpdate) {
      const currentCell = this.cellData[row][col];
      const char = getCellChar(currentCell);
      const existingBg = getCellBg(currentCell) as ColorCode | undefined;

      if (char !== ' ') {
        if (color === 'W' && !existingBg) {
          this.cellData[row][col] = char;
        } else if (color === 'W' && existingBg) {
          this.cellData[row][col] = { char, bg: existingBg };
        } else if (existingBg) {
          this.cellData[row][col] = { char, color, bg: existingBg };
        } else {
          this.cellData[row][col] = { char, color };
        }
      }
    }

    this.render();

    if (this.onColorChange) {
      this.onColorChange(color);
    }
  }

  /**
   * Set the selected background color
   * Also applies the background to selected cells (or current cursor cell if no selection)
   */
  setSelectedBg(bg: ColorCode | null): void {
    if (this.selectedBg === bg) return; // Prevent loops

    this.selectedBg = bg;

    // Apply to all selected cells (or just cursor cell if no selection)
    const bounds = this.getSelectionBounds();
    const cellsToUpdate = bounds
      ? this.getCellsInBounds(bounds)
      : [{ row: this.cursorRow, col: this.cursorCol }];

    for (const { row, col } of cellsToUpdate) {
      const currentCell = this.cellData[row][col];
      const char = getCellChar(currentCell);
      const existingColor = (getCellColor(currentCell) || 'W') as ColorCode;

      // Update the cell with new background
      if (!bg && existingColor === 'W') {
        this.cellData[row][col] = char;
      } else if (!bg) {
        this.cellData[row][col] = { char, color: existingColor };
      } else if (existingColor === 'W') {
        this.cellData[row][col] = { char, bg };
      } else {
        this.cellData[row][col] = { char, color: existingColor, bg };
      }
    }

    this.render();

    if (this.onBgChange) {
      this.onBgChange(bg);
    }
  }

  /**
   * Get current selected color
   */
  getSelectedColor(): ColorCode {
    return this.selectedColor;
  }

  /**
   * Get current selected background
   */
  getSelectedBg(): ColorCode | null {
    return this.selectedBg;
  }

  /**
   * Set color change callback
   */
  setOnColorChange(callback: ColorChangeCallback): void {
    this.onColorChange = callback;
  }

  /**
   * Set background change callback
   */
  setOnBgChange(callback: BgChangeCallback): void {
    this.onBgChange = callback;
  }

  /**
   * Set cell data from external source
   */
  setCells(cells: CellData[][]): void {
    this.cellData = cells.map((row) => [...row]);
    this.render();
  }

  /**
   * Get current cell data
   */
  getCells(): CellData[][] {
    return this.cellData.map((row) => [...row]);
  }

  /**
   * Resize the editor
   */
  setSize(rows: number, cols: number): void {
    // Clear selection on resize
    this.clearSelection();

    // Create new cell data, preserving existing content where possible
    const newCellData = createEmptyCells(rows, cols);
    for (let row = 0; row < Math.min(rows, this.rows); row++) {
      for (let col = 0; col < Math.min(cols, this.cols); col++) {
        newCellData[row][col] = this.cellData[row][col];
      }
    }

    this.rows = rows;
    this.cols = cols;
    this.cellData = newCellData;

    // Ensure cursor is within bounds
    if (this.cursorRow >= rows) this.cursorRow = rows - 1;
    if (this.cursorCol >= cols) this.cursorCol = cols - 1;

    // Rebuild grid
    this.buildGrid();

    // Re-add grid-level mouse event listeners after rebuild
    this.gridElement.addEventListener('mousemove', this.handleMouseMove.bind(this));
    this.gridElement.addEventListener('mouseup', this.handleMouseUp.bind(this));
    this.gridElement.addEventListener('mouseleave', this.handleMouseUp.bind(this));

    this.render();
  }

  /**
   * Convert cell data to text with color and background markup
   * Compatible with existing message format
   */
  toMarkupText(): string {
    // In raw mode, just return the textarea content
    if (this.mode === 'raw') {
      return this.rawTextarea.value;
    }

    const lines: string[] = [];

    for (let row = 0; row < this.rows; row++) {
      let line = '';
      let currentColor: ColorCode = 'W';
      let currentBg: ColorCode | null = null;

      for (let col = 0; col < this.cols; col++) {
        const cell = this.cellData[row][col];
        const char = getCellChar(cell);
        const color = (getCellColor(cell) || 'W') as ColorCode;
        const bg = (getCellBg(cell) || null) as ColorCode | null;

        // Check if this cell is part of a clock token
        const clockMeta = typeof cell === 'object' && 'clock' in cell ? cell.clock : undefined;

        // Insert background code when background changes
        if (bg !== currentBg) {
          if (bg) {
            line += `[${bg}]`;
          } else {
            line += '[-]'; // Clear background
          }
          currentBg = bg;
        }

        // Insert color code when color changes
        if (color !== currentColor) {
          line += `{${color}}`;
          currentColor = color;
        }

        // If this is the start of a clock sequence, insert the token and skip ahead
        if (clockMeta && clockMeta.index === 0) {
          line += `<CLOCK:${clockMeta.format}>`;

          // Skip ahead past all cells in this clock sequence
          // Find how many cells this clock spans by checking consecutive cells with same format
          let clockLength = 1;
          while (col + clockLength < this.cols) {
            const nextCell = this.cellData[row][col + clockLength];
            const nextClockMeta = typeof nextCell === 'object' && 'clock' in nextCell ? nextCell.clock : undefined;
            if (nextClockMeta && nextClockMeta.format === clockMeta.format && nextClockMeta.index === clockLength) {
              clockLength++;
            } else {
              break;
            }
          }

          // Skip past the clock cells (we've already output the token)
          col += clockLength - 1;
        } else if (!clockMeta) {
          // Regular character (not part of a clock)
          line += char;
        }
        // If clockMeta exists but index !== 0, skip it (already handled by the token)
      }

      // Don't trim trailing whitespace - it may have background colors applied
      lines.push(line);
    }

    return lines.join('\n');
  }

  /**
   * Clear all cells
   */
  clear(): void {
    this.cellData = createEmptyCells(this.rows, this.cols);
    this.cursorRow = 0;
    this.cursorCol = 0;

    // If in raw mode, clear the textarea too
    if (this.mode === 'raw') {
      this.rawTextarea.value = '';
    } else {
      this.render();
    }
  }

  /**
   * Get current dimensions
   */
  getDimensions(): { rows: number; cols: number } {
    return { rows: this.rows, cols: this.cols };
  }

  /**
   * Get current editor mode
   */
  getMode(): EditorMode {
    return this.mode;
  }

  /**
   * Set editor mode (wysiwyg or raw)
   */
  setMode(mode: EditorMode): void {
    if (this.mode === mode) return;

    // Clear selection when switching modes
    this.clearSelection();

    if (mode === 'raw') {
      // Switching to raw mode - convert grid to text
      this.rawTextarea.value = this.toMarkupText();
      this.gridElement.style.display = 'none';
      this.rawTextarea.style.display = 'block';
      this.rawTextarea.focus();
    } else {
      // Switching to WYSIWYG - parse text back to grid
      this.parseRawText(this.rawTextarea.value);
      this.rawTextarea.style.display = 'none';
      this.gridElement.style.display = 'grid';
      this.focus();
    }

    this.mode = mode;
  }

  /**
   * Parse raw text with color and background markup into cell data
   */
  private parseRawText(text: string): void {
    const lines = text.split('\n');
    this.cellData = createEmptyCells(this.rows, this.cols);

    for (let row = 0; row < this.rows && row < lines.length; row++) {
      const line = lines[row];
      // Use parseColorMarkup to handle all markup (colors, backgrounds, clocks)
      const parsed = parseColorMarkup(line);

      for (let col = 0; col < this.cols && col < parsed.length; col++) {
        const { char, color, bg, clock } = parsed[col];
        const normalizedChar = CHARSET.includes(char.toUpperCase()) ? char.toUpperCase() : ' ';

        // Build cell data based on what's set
        if (color === 'W' && !bg && !clock) {
          this.cellData[row][col] = normalizedChar;
        } else {
          // Create cell object with all non-default fields
          const cell: CellData = { char: normalizedChar };
          if (typeof cell === 'object') {
            if (color !== 'W') cell.color = color;
            if (bg) cell.bg = bg;
            if (clock) cell.clock = clock;
          }
          this.cellData[row][col] = cell;
        }
      }
    }

    this.cursorRow = 0;
    this.cursorCol = 0;
    this.render();
  }
}

/**
 * Color Toolbar Component with TEXT and BG selectors
 */
export class ColorToolbar {
  private container: HTMLElement;
  private textButtons: Map<ColorCode, HTMLButtonElement> = new Map();
  private bgButtons: Map<ColorCode | 'none', HTMLButtonElement> = new Map();
  private modeToggleBtn: HTMLButtonElement | null = null;
  private centerBtn: HTMLButtonElement | null = null;
  private clearBtn: HTMLButtonElement | null = null;
  private textPopover: HTMLElement | null = null;
  private bgPopover: HTMLElement | null = null;
  private textSwatchEl: HTMLElement | null = null;
  private bgSwatchEl: HTMLElement | null = null;
  private textColorBtn: HTMLButtonElement | null = null;
  private bgColorBtn: HTMLButtonElement | null = null;
  private selectedColor: ColorCode = 'W';
  private selectedBg: ColorCode | null = null;
  private currentMode: EditorMode = 'wysiwyg';
  private onColorChange: ColorChangeCallback;
  private onBgChange?: BgChangeCallback;
  private onModeChange?: ModeChangeCallback;
  private onCenterAlign?: CenterAlignCallback;
  private onClear?: ClearCallback;

  constructor(
    container: HTMLElement,
    onColorChange: ColorChangeCallback,
    onBgChange?: BgChangeCallback,
    onModeChange?: ModeChangeCallback,
    onCenterAlign?: CenterAlignCallback,
    onClear?: ClearCallback
  ) {
    this.container = container;
    this.onColorChange = onColorChange;
    this.onBgChange = onBgChange;
    this.onModeChange = onModeChange;
    this.onCenterAlign = onCenterAlign;
    this.onClear = onClear;

    this.build();
  }

  private build(): void {
    this.container.innerHTML = `
      <div class="toolbar-row">
        <button class="toolbar-action-btn center-align-btn" title="Center align selection">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M2 4h12M4 8h8M2 12h12"/>
          </svg>
          <span>Center</span>
        </button>
        <button class="toolbar-action-btn mode-toggle-btn" title="Toggle raw text mode">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M4 3L1 6l3 3M12 3l3 3-3 3M10 1L6 15"/>
          </svg>
          <span>Raw</span>
        </button>
        <div class="toolbar-divider"></div>
        <div class="color-picker-wrapper">
          <button class="color-picker-btn text-color-btn" title="Text color">
            <span class="color-swatch text-swatch"></span>
            <span>Text</span>
            <svg viewBox="0 0 12 12" fill="currentColor" width="8" height="8" class="dropdown-arrow">
              <path d="M2 4l4 4 4-4"/>
            </svg>
          </button>
          <div class="color-popover text-popover hidden">
            <div class="color-buttons text-colors"></div>
          </div>
        </div>
        <div class="color-picker-wrapper">
          <button class="color-picker-btn bg-color-btn" title="Background color">
            <span class="color-swatch bg-swatch"></span>
            <span>Bg</span>
            <svg viewBox="0 0 12 12" fill="currentColor" width="8" height="8" class="dropdown-arrow">
              <path d="M2 4l4 4 4-4"/>
            </svg>
          </button>
          <div class="color-popover bg-popover hidden">
            <div class="color-buttons bg-colors"></div>
          </div>
        </div>
        <div class="toolbar-spacer"></div>
        <button class="toolbar-action-btn clear-btn" title="Clear all cells (requires confirmation)">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M2 4h12M5 4V3a1 1 0 011-1h4a1 1 0 011 1v1M6 7v5M10 7v5M3 4l1 9a1 1 0 001 1h6a1 1 0 001-1l1-9"/>
          </svg>
          <span>Clear</span>
        </button>
      </div>
    `;

    // Build text color buttons
    const textContainer = this.container.querySelector('.text-colors')!;
    COLOR_CODES.forEach((code) => {
      const button = document.createElement('button');
      button.className = 'color-btn';
      button.dataset.color = code;
      button.style.backgroundColor = COLOR_MAP[code];
      button.setAttribute('aria-label', `Text color ${code}`);
      button.title = `Text: ${code}`;

      if (code === this.selectedColor) {
        button.classList.add('selected');
      }

      button.addEventListener('click', () => {
        this.setSelectedColor(code);
        this.closeAllPopovers();
      });

      textContainer.appendChild(button);
      this.textButtons.set(code, button);
    });

    // Build background color buttons
    const bgContainer = this.container.querySelector('.bg-colors')!;

    // Add "none" button first
    const noneBtn = document.createElement('button');
    noneBtn.className = 'color-btn bg-none selected';
    noneBtn.setAttribute('aria-label', 'No background');
    noneBtn.title = 'No background';
    noneBtn.innerHTML = `<svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
      <line x1="3" y1="3" x2="13" y2="13" stroke="currentColor" stroke-width="2"/>
      <line x1="13" y1="3" x2="3" y2="13" stroke="currentColor" stroke-width="2"/>
    </svg>`;
    noneBtn.addEventListener('click', () => {
      this.setSelectedBg(null);
      this.closeAllPopovers();
    });
    bgContainer.appendChild(noneBtn);
    this.bgButtons.set('none', noneBtn);

    // Add color buttons for background
    COLOR_CODES.forEach((code) => {
      const button = document.createElement('button');
      button.className = 'color-btn';
      button.dataset.bg = code;
      button.style.backgroundColor = COLOR_MAP[code];
      button.setAttribute('aria-label', `Background color ${code}`);
      button.title = `Background: ${code}`;

      button.addEventListener('click', () => {
        this.setSelectedBg(code);
        this.closeAllPopovers();
      });

      bgContainer.appendChild(button);
      this.bgButtons.set(code, button);
    });

    // Center align button
    this.centerBtn = this.container.querySelector('.center-align-btn');
    if (this.centerBtn) {
      this.centerBtn.addEventListener('click', () => {
        if (this.onCenterAlign) {
          this.onCenterAlign();
        }
      });
    }

    // Clear button
    this.clearBtn = this.container.querySelector('.clear-btn');
    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', () => {
        if (confirm('Clear all cells? This action cannot be undone.')) {
          if (this.onClear) {
            this.onClear();
          }
        }
      });
    }

    // Mode toggle button
    this.modeToggleBtn = this.container.querySelector('.mode-toggle-btn');
    if (this.modeToggleBtn) {
      this.modeToggleBtn.addEventListener('click', () => {
        const newMode = this.currentMode === 'wysiwyg' ? 'raw' : 'wysiwyg';
        this.setMode(newMode);
        if (this.onModeChange) {
          this.onModeChange(newMode);
        }
      });
    }

    // Wire up color picker popovers
    this.textPopover = this.container.querySelector('.text-popover');
    this.bgPopover = this.container.querySelector('.bg-popover');
    this.textSwatchEl = this.container.querySelector('.text-swatch');
    this.bgSwatchEl = this.container.querySelector('.bg-swatch');
    this.textColorBtn = this.container.querySelector('.text-color-btn');
    this.bgColorBtn = this.container.querySelector('.bg-color-btn');

    if (this.textColorBtn) {
      this.textColorBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.togglePopover(this.textPopover);
      });
    }

    if (this.bgColorBtn) {
      this.bgColorBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.togglePopover(this.bgPopover);
      });
    }

    // Close popovers when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.container.contains(e.target as Node)) {
        this.closeAllPopovers();
      }
    });

    // Update swatches to show current colors
    this.updateSwatches();
  }

  private togglePopover(popover: HTMLElement | null): void {
    if (!popover) return;

    const isCurrentlyHidden = popover.classList.contains('hidden');

    // Close all popovers first
    this.closeAllPopovers();

    // If it was hidden, show it
    if (isCurrentlyHidden) {
      popover.classList.remove('hidden');
    }
  }

  private closeAllPopovers(): void {
    this.textPopover?.classList.add('hidden');
    this.bgPopover?.classList.add('hidden');
  }

  private updateSwatches(): void {
    if (this.textSwatchEl) {
      this.textSwatchEl.style.backgroundColor = COLOR_MAP[this.selectedColor];
    }
    if (this.bgSwatchEl) {
      if (this.selectedBg) {
        this.bgSwatchEl.style.backgroundColor = COLOR_MAP[this.selectedBg];
        this.bgSwatchEl.style.backgroundImage = 'none';
      } else {
        // Show "no background" pattern (diagonal line)
        this.bgSwatchEl.style.backgroundColor = '#f5f5f0';
        this.bgSwatchEl.style.backgroundImage = 'linear-gradient(135deg, transparent 30%, #999 30%, #999 50%, transparent 50%, transparent 80%, #999 80%)';
        this.bgSwatchEl.style.backgroundSize = '8px 8px';
      }
    }
  }

  setSelectedColor(color: ColorCode): void {
    if (this.selectedColor === color) return;

    // Update visual state
    this.textButtons.get(this.selectedColor)?.classList.remove('selected');
    this.selectedColor = color;
    this.textButtons.get(color)?.classList.add('selected');

    // Update swatch
    this.updateSwatches();

    // Notify callback
    this.onColorChange(color);
  }

  setSelectedBg(bg: ColorCode | null): void {
    if (this.selectedBg === bg) return;

    // Update visual state - deselect previous
    if (this.selectedBg === null) {
      this.bgButtons.get('none')?.classList.remove('selected');
    } else {
      this.bgButtons.get(this.selectedBg)?.classList.remove('selected');
    }

    this.selectedBg = bg;

    // Select new
    if (bg === null) {
      this.bgButtons.get('none')?.classList.add('selected');
    } else {
      this.bgButtons.get(bg)?.classList.add('selected');
    }

    // Update swatch
    this.updateSwatches();

    // Notify callback
    if (this.onBgChange) {
      this.onBgChange(bg);
    }
  }

  getSelectedColor(): ColorCode {
    return this.selectedColor;
  }

  getSelectedBg(): ColorCode | null {
    return this.selectedBg;
  }

  setMode(mode: EditorMode): void {
    this.currentMode = mode;
    this.modeToggleBtn?.classList.toggle('raw-mode', mode === 'raw');
  }

  getMode(): EditorMode {
    return this.currentMode;
  }
}
